<script lang="ts" setup>
import { computed } from 'vue';
import { startCase } from 'lodash-es';
import DetailInformation from '@/common/components/molecules/DetailInformation.vue';
import LabelItem from '@/common/components/molecules/labelItem/LabelItem.vue';
import LabelItemGroup from '@/common/components/templates/labelItemGroup/LabelItemGroup.vue';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import { useInternational } from '@/common/locale';
import { TARGET_KIND_LOCALE_MAP } from '@/alert/utils/define';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import type { SystemAlertDetail } from '@/api-client/alert/model/system-alert-detail';

interface Props {
  systemAlertDetails: SystemAlertDetail[];
}

const props = defineProps<Props>();

const { t } = useInternational();

const systemAlertPlatform = computed(() => {
  if (props.systemAlertDetails.length === 0) return '';
  const targetKind = props.systemAlertDetails[0].targetKind ?? '';
  const localeKey = TARGET_KIND_LOCALE_MAP[targetKind];
  return localeKey ? t(localeKey) : startCase(targetKind);
});

const systemAlertRuleCriteria = computed(() => {
  if (props.systemAlertDetails.length === 0) return '';
  return props.systemAlertDetails[0].ruleCriteria ?? '';
});
</script>

<template>
  <detail-information
    :title="t('WORD.RULE_DATA')"
    use-expand
    :expand-env-key="USER_ENV_KEYS.TOGGLE_alert_eventAlertDetail_ruleData_expanded"
    class="system-rule-data-section"
  >
    <template #content>
      <div class="system-rule-data-section__rule-common">
        <label-item-group>
          <label-item :label="t('WORD.TYPE')">{{ t('WORD.SYSTEM') }}</label-item>
          <label-item :label="t('WORD.RULE_CRITERIA')">
            <status-tag
              v-if="systemAlertRuleCriteria"
              type="inactive"
            >
              {{ systemAlertRuleCriteria }}
            </status-tag>
          </label-item>
        </label-item-group>
        <label-item-group>
          <label-item :label="t('WORD.PLATFORM')">
            {{ systemAlertPlatform }}
          </label-item>
        </label-item-group>
      </div>
    </template>
  </detail-information>
</template>

<style scoped lang="scss">
.system-rule-data-section {
  :deep(.detail-information__content) {
    padding: 16px 10px 12px;
  }

  :deep(.label-item-group) {
    padding: 0 4px;
  }

  :deep(.label-item) {
    margin-bottom: 12px;
  }

  :deep(.label-item__label) {
    color: var(--alert-rule-item-label-color);
  }

  :deep(.label-item__body) {
    color: var(--main-font-color);
  }

  :deep(.label-item::after) {
    background-color: var(--alert-rule-card-border-color);
  }

  :deep(.status-tag) {
    min-width: 50px;
    padding: 2px 4px 1px;
  }

  &__rule-common {
    padding-top: 4px;
  }
}
</style>
